import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext();

// Complete translations with Hindi in Devanagari
const translations = {
  en: {
    // Navigation
    home: "Home",
    dashboard: "Dashboard",
    horoscope: "Horoscope",
    kundli: "Kundli",
    compatibility: "Match",
    ask: "Ask",
    mantras: "Mantras",
    rituals: "Rituals",
    learn: "Learn",
    kids: "Kids",
    family: "Family",
    settings: "Settings",
    reports: "Reports",
    tools: "Tools",
    insights: "My Stars",
    consultation: "Consultation",
    prashna: "Prashna Kundali",
    varshphal: "Varshphal",
    
    // Common
    welcome: "Welcome",
    today: "Today",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    download: "Download",
    share: "Share",
    back: "Back",
    next: "Next",
    submit: "Submit",
    close: "Close",
    yes: "Yes",
    no: "No",
    view: "View",
    search: "Search",
    filter: "Filter",
    
    // Specific
    dailyHoroscope: "Daily Horoscope",
    birthChart: "Birth Chart",
    moonSign: "Moon Sign",
    sunSign: "Sun Sign",
    askQuestion: "Ask a Question",
    sacredMantras: "Sacred Mantras",
    familyDashboard: "Family Dashboard",
    
    // Panchang
    tithi: "Tithi",
    nakshatra: "Nakshatra",
    yoga: "Yoga",
    karana: "Karana",
    sunrise: "Sunrise",
    sunset: "Sunset",
    rahuKalam: "Rahu Kalam",
    
    // Kundli
    ascendant: "Ascendant (Lagna)",
    planetaryPositions: "Planetary Positions",
    dasha: "Dasha",
    yogas: "Yogas",
    
    // PWA/Offline
    offline: "You are offline",
    online: "Back online",
    installApp: "Install App",
    offlineReady: "Ready for offline use",
    updateAvailable: "Update available",
    
    // Prashna & Varshphal
    prashnaTitle: "Prashna Kundali",
    askYourQuestion: "Ask Your Question",
    questionCategory: "Question Category",
    verdict: "Verdict",
    positiveFactors: "Positive Factors",
    negativeFactors: "Negative Factors",
    varshphalTitle: "Varshphal",
    yearLord: "Year Lord",
    muntha: "Muntha",
    annualPredictions: "Annual Predictions",
    selectYear: "Select Year"
  },
  hi: {
    // Navigation
    home: "होम",
    dashboard: "डैशबोर्ड",
    horoscope: "राशिफल",
    kundli: "कुंडली",
    compatibility: "मिलान",
    ask: "पूछें",
    mantras: "मंत्र",
    rituals: "अनुष्ठान",
    learn: "सीखें",
    kids: "बच्चे",
    family: "परिवार",
    settings: "सेटिंग्स",
    reports: "रिपोर्ट",
    tools: "उपकरण",
    insights: "मेरे सितारे",
    consultation: "परामर्श",
    prashna: "प्रश्न कुंडली",
    varshphal: "वर्षफल",
    
    // Common
    welcome: "स्वागत है",
    today: "आज",
    loading: "लोड हो रहा है...",
    save: "सहेजें",
    cancel: "रद्द करें",
    download: "डाउनलोड",
    share: "साझा करें",
    back: "वापस",
    next: "आगे",
    submit: "भेजें",
    close: "बंद करें",
    yes: "हाँ",
    no: "नहीं",
    view: "देखें",
    search: "खोजें",
    filter: "फ़िल्टर",
    
    // Specific
    dailyHoroscope: "दैनिक राशिफल",
    birthChart: "जन्म कुंडली",
    moonSign: "चंद्र राशि",
    sunSign: "सूर्य राशि",
    askQuestion: "प्रश्न पूछें",
    sacredMantras: "पवित्र मंत्र",
    familyDashboard: "परिवार डैशबोर्ड",
    
    // Panchang
    tithi: "तिथि",
    nakshatra: "नक्षत्र",
    yoga: "योग",
    karana: "करण",
    sunrise: "सूर्योदय",
    sunset: "सूर्यास्त",
    rahuKalam: "राहु काल",
    
    // Kundli
    ascendant: "लग्न",
    planetaryPositions: "ग्रह स्थिति",
    dasha: "दशा",
    yogas: "योग",
    
    // PWA/Offline
    offline: "आप ऑफ़लाइन हैं",
    online: "वापस ऑनलाइन",
    installApp: "ऐप इंस्टॉल करें",
    offlineReady: "ऑफ़लाइन उपयोग के लिए तैयार",
    updateAvailable: "अपडेट उपलब्ध है",
    
    // Prashna & Varshphal
    prashnaTitle: "प्रश्न कुंडली",
    askYourQuestion: "अपना प्रश्न पूछें",
    questionCategory: "प्रश्न श्रेणी",
    verdict: "निर्णय",
    positiveFactors: "सकारात्मक संकेत",
    negativeFactors: "नकारात्मक संकेत",
    varshphalTitle: "वर्षफल",
    yearLord: "वर्षेश",
    muntha: "मुंथा",
    annualPredictions: "वार्षिक भविष्यफल",
    selectYear: "वर्ष चुनें"
  }
};

// Astrological term translations
const astroTerms = {
  rashis: {
    en: {
      "Aries": "Aries", "Taurus": "Taurus", "Gemini": "Gemini",
      "Cancer": "Cancer", "Leo": "Leo", "Virgo": "Virgo",
      "Libra": "Libra", "Scorpio": "Scorpio", "Sagittarius": "Sagittarius",
      "Capricorn": "Capricorn", "Aquarius": "Aquarius", "Pisces": "Pisces"
    },
    hi: {
      "Aries": "मेष", "Taurus": "वृषभ", "Gemini": "मिथुन",
      "Cancer": "कर्क", "Leo": "सिंह", "Virgo": "कन्या",
      "Libra": "तुला", "Scorpio": "वृश्चिक", "Sagittarius": "धनु",
      "Capricorn": "मकर", "Aquarius": "कुंभ", "Pisces": "मीन"
    }
  },
  planets: {
    en: {
      "Sun": "Sun", "Moon": "Moon", "Mars": "Mars",
      "Mercury": "Mercury", "Jupiter": "Jupiter", "Venus": "Venus",
      "Saturn": "Saturn", "Rahu": "Rahu", "Ketu": "Ketu"
    },
    hi: {
      "Sun": "सूर्य", "Moon": "चंद्र", "Mars": "मंगल",
      "Mercury": "बुध", "Jupiter": "बृहस्पति", "Venus": "शुक्र",
      "Saturn": "शनि", "Rahu": "राहु", "Ketu": "केतु"
    }
  },
  weekdays: {
    en: {
      "Sunday": "Sunday", "Monday": "Monday", "Tuesday": "Tuesday",
      "Wednesday": "Wednesday", "Thursday": "Thursday", "Friday": "Friday",
      "Saturday": "Saturday"
    },
    hi: {
      "Sunday": "रविवार", "Monday": "सोमवार", "Tuesday": "मंगलवार",
      "Wednesday": "बुधवार", "Thursday": "गुरुवार", "Friday": "शुक्रवार",
      "Saturday": "शनिवार"
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || "en";
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastSyncedLanguage = useRef(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("language", language);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
    
    // Sync user preference to backend only when language actually changed
    // (avoids redundant PUTs on every mount / connectivity toggle)
    const token = localStorage.getItem("prana_token");
    if (token && isOnline && lastSyncedLanguage.current !== language) {
      lastSyncedLanguage.current = language;
      fetch(`${API}/api/i18n/user/language`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ language })
      }).catch(console.error);
    }
  }, [language, isOnline]);

  // Translation function
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  }, [language]);

  // Translate rashi name
  const translateRashi = useCallback((rashi) => {
    if (!rashi) return rashi;
    return astroTerms.rashis[language]?.[rashi] || rashi;
  }, [language]);

  // Translate planet name
  const translatePlanet = useCallback((planet) => {
    if (!planet) return planet;
    return astroTerms.planets[language]?.[planet] || planet;
  }, [language]);

  // Translate weekday
  const translateWeekday = useCallback((weekday) => {
    if (!weekday) return weekday;
    return astroTerms.weekdays[language]?.[weekday] || weekday;
  }, [language]);

  // Change language
  const changeLanguage = useCallback((lang) => {
    if (lang === "en" || lang === "hi") {
      setLanguage(lang);
    }
  }, []);

  // Check if current language is Hindi
  const isHindi = language === "hi";

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage, 
      t,
      translateRashi,
      translatePlanet,
      translateWeekday,
      isHindi,
      isOnline
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export default LanguageContext;
